import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useRecoilState, useRecoilValue } from "recoil"

import {
  activeFileSelector,
  activePluginSelector,
} from "../../../recoil/pluginStudio/selectors"
import {
  CodeEditorState,
  Plugin,
  UserRole,
} from "../../../recoil/pluginStudio/types"

/**
 * Hook for managing the Monaco editor integration
 * This focuses exclusively on editor concerns, while data is managed by Recoil
 */
export function usePluginEditor() {
  // Reference to Monaco editor instance
  const editorRef = useRef<any>(null)

  // Get data from Recoil
  const activeFile = useRecoilValue(activeFileSelector)
  const activePlugin = useRecoilValue(activePluginSelector)
  const [_, setActiveFile] = useRecoilState(activeFileSelector)

  // Local UI state
  const [isEditorReady, setIsEditorReady] = useState(false)
  const [showTranspiledCode, setShowTranspiledCode] = useState(false)

  // Determine the view type based on the active file
  const viewType = useMemo<UserRole>(() => {
    return (activeFile?.type as UserRole) || UserRole.GUEST
  }, [activeFile])

  // Create a memo of the editor state
  const editorState = useMemo<CodeEditorState>(
    () => ({
      currentCode: activeFile?.content || "",
      transpiledCode: activeFile?.transpiledContent || "",
      showTranspiledCode,
      viewType,
    }),
    [activeFile, showTranspiledCode, viewType],
  )

  // Toggle between source and transpiled views
  const toggleTranspiledView = useCallback(() => {
    setShowTranspiledCode((prev) => !prev)
  }, [])

  // Update editor when active file changes
  useEffect(() => {
    if (editorRef.current && activeFile) {
      const content = showTranspiledCode
        ? activeFile.transpiledContent || "// No transpiled code available"
        : activeFile.content || ""

      // Update the editor content if it doesn't match current value
      const currentValue = editorRef.current.getValue()
      if (currentValue !== content) {
        console.log("[EDITOR] Updating editor content due to file change")
        editorRef.current.setValue(content)
      }
    }
  }, [
    activeFile?.id,
    activeFile?.content,
    activeFile?.transpiledContent,
    showTranspiledCode,
  ])

  // Handle editor content changes (used by Monaco onChange)
  const handleCodeChange = useCallback(
    (newCode: string) => {
      if (!activeFile || showTranspiledCode) return

      // Update Recoil state directly
      setActiveFile(newCode as any)

      console.log(
        `[EDITOR] Code updated in editor, new length: ${newCode.length}`,
      )
    },
    [activeFile, setActiveFile, showTranspiledCode],
  )

  // Handle editor mounting
  const handleEditorMount = useCallback((editor: any, monaco: any) => {
    console.log("[EDITOR] Monaco editor mounted")
    editorRef.current = editor
    setIsEditorReady(true)

    // Setup Monaco type definitions and configuration
    setupMonacoEditor(monaco)
  }, [])

  return {
    editorRef,
    editorState,
    isEditorReady,
    handleEditorMount,
    handleCodeChange,
    toggleTranspiledView,
    showTranspiledCode,
    viewType,
  }
}

// Helper function to setup Monaco editor configuration
function setupMonacoEditor(monaco: any) {
  // Register .tsx extension with TypeScript
  monaco.languages.typescript.typescriptDefaults.setExtraLibs([])
  monaco.languages.typescript.javascriptDefaults.setExtraLibs([])

  // Add React typings
  monaco.languages.typescript.typescriptDefaults.setCompilerOptions({
    jsx: monaco.languages.typescript.JsxEmit.React,
    jsxFactory: "React.createElement",
    reactNamespace: "React",
    allowNonTsExtensions: true,
    target: monaco.languages.typescript.ScriptTarget.ES2016,
    allowJs: true,
    moduleResolution: monaco.languages.typescript.ModuleResolutionKind.NodeJs,
    module: monaco.languages.typescript.ModuleKind.ESNext,
    noEmit: true,
    typeRoots: ["node_modules/@types"],
    strict: false,
  })

  // Define basic React types
  const reactDts = `
    declare module "react" {
      export = React;
    }
    
    declare namespace React {
      function createElement(type: any, props?: any, ...children: any[]): any;
      function Fragment(props: any): any;
      
      type FC<P = {}> = FunctionComponent<P>;
      interface FunctionComponent<P = {}> {
        (props: P): any;
      }
      
      type CSSProperties = any;
      
      interface HTMLAttributes<T> {
        className?: string;
        style?: CSSProperties;
        onClick?: (e: MouseEvent) => void;
        onChange?: (e: Event) => void;
        [key: string]: any;
      }
      
      interface MouseEvent {
        preventDefault(): void;
        stopPropagation(): void;
      }
      
      interface Event {
        preventDefault(): void;
        stopPropagation(): void;
        target: EventTarget;
      }
      
      interface EventTarget {
        value?: any;
        [key: string]: any;
      }
      
      interface ReactElement<P = any> {
        type: any;
        props: P;
        key: any;
      }
      
      const useState: <T>(initialState: T) => [T, (newState: T) => void];
      const useEffect: (effect: () => void | (() => void), deps?: any[]) => void;
      const useCallback: <T extends (...args: any[]) => any>(callback: T, deps?: any[]) => T;
      const useMemo: <T>(factory: () => T, deps?: any[]) => T;
      const useRef: <T>(initialValue: T) => { current: T };
      
      // Add more React types as needed...
    }
  `

  // Add the basic React typings
  monaco.languages.typescript.typescriptDefaults.addExtraLib(
    reactDts,
    "file:///node_modules/@types/react/index.d.ts",
  )

  // Add custom plugin typings
  const customPluginDts = `
    // Plugin API type definitions
    declare namespace PluginAPI {
      interface EventPluginData {
        [key: string]: any;
      }
      
      interface UserPluginData {
        [key: string]: any;
      }
      
      interface PluginConfig {
        [key: string]: any;
      }
      
      interface EventData {
        id: string;
        name: string;
        description?: string;
        startDate?: string;
        endDate?: string;
        location?: {
          address?: string;
          lat?: number;
          lng?: number;
        };
        [key: string]: any;
      }
      
      interface UserData {
        id: string;
        displayName?: string;
        email?: string;
        photoURL?: string;
        [key: string]: any;
      }
      
      // API methods exposed to the plugin
      interface API {
        getPluginConfig(): Promise<PluginConfig>;
        saveConfig(config: PluginConfig): Promise<void>;
        getEventData(): Promise<EventPluginData>;
        saveEventData(data: Partial<EventPluginData>): Promise<void>;
        getUserData(): Promise<UserPluginData>;
        saveUserData(data: Partial<UserPluginData>): Promise<void>;
      }
      
      // Theme and styling utilities
      interface Theme {
        colors: {
          primary: string;
          secondary: string;
          background: string;
          text: string;
          error: string;
          success: string;
          // Add more as needed
        };
        spacing: {
          xs: string;
          sm: string;
          md: string;
          lg: string;
          xl: string;
        };
      }
      
      // Common UI components that could be provided
      namespace Components {
        interface ButtonProps {
          text: string;
          onClick: () => void;
          variant?: 'primary' | 'secondary' | 'outline';
          size?: 'small' | 'medium' | 'large';
          disabled?: boolean;
          loading?: boolean;
          icon?: React.ReactNode;
          className?: string;
        }
        const Button: React.FC<ButtonProps>;
        
        // Add more component definitions as needed
      }
    }
    
    // Global variables available to plugins
    declare var api: PluginAPI.API;
    declare var theme: PluginAPI.Theme;
    declare var components: PluginAPI.Components;
    declare var eventData: PluginAPI.EventData;
    declare var userData: PluginAPI.UserData;
    
    // Global plugin function
    declare function PluginComponent(props: {}): React.ReactElement;
  `

  monaco.languages.typescript.typescriptDefaults.addExtraLib(
    customPluginDts,
    "file:///static/plugin-types.d.ts",
  )
}
